import { useState } from 'react'
import { BasketIcon, IconParkSolidDownOne, IconParkSolidUpOne, PaperList } from '../../../assets/icons/Icons'
import InformationModal from '../../modals/InformationModal';
import { OrderData } from '../../../types/order';
import { formatDate, formatTime } from '../../../utils/formatDateAndTime';
import Loading from '../../ui/Loading';
import ErrorMessage from '../../ui/ErrorMessage';
import { showToast } from '../../../toastify/Toast';
import { getBorderColorStyle, getTextColorStyle } from '../../../utils/statusStyles';
import { formatChapelNumbers } from '../../../utils/chapelsFormater';
import { useMutationData } from '../../../hooks/api/useMutationData';
import { STATUS } from '../../../constant/constant';

export interface PropsModalInformationTypes {
    typeOfModal: string;
    labelInfo: string
}

interface UserOrderListProps {
    order: OrderData[];
    openOrder: number | null;
    handleToggle: (id: number) => void
    isLoading: boolean
    isError: boolean
    errorMessage: string;
    storeOpen: boolean;
}

export default function UserOrderList({ storeOpen, openOrder, handleToggle, order, isLoading, isError, errorMessage }: UserOrderListProps) {

    const [showCancelledModal, setShowCancelledModal] = useState<boolean>(false)
    const [orderId, setOrderId] = useState<number | null>(null)
    const { cancelledMyOrder, isLoadingCancelledMyOrder } = useMutationData()
    const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({});
    const statusMessage = {
        Pending: "Your order is awaiting review.",
        Processing: "Your order is in progress.",
        Completed: "You may now claim your order.",
        Cancelled: "The order has been cancelled.",
    };

    const handleCancelledOrder = (id: number) => {
        setOrderId(id)
        setShowCancelledModal(true)
    }

    const handleCancelOrder = async () => {
        if (!orderId) return;

        try {
            await cancelledMyOrder(orderId).unwrap();
            showToast('Order cancelled successfully.', 'success');
            setShowCancelledModal(false);
        } catch (err: any) {
            const message = err?.data?.message || 'Failed to cancel order.';
            showToast(message, 'error');
        }
    };

    const toggleItem = (index: number) => {
        setExpandedItems(prev => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    return (
        <>  
            <div className='flex-1 overflow-y-auto p-2 space-y-3 max-h-none'>
                {order.length === 0 ? (
                    <div className="text-center text-gray-500 text-sm mt-5">
                        No orders found
                    </div>
                ) : isLoading ? (
                    <Loading message='Loading'/>
                ) : isError ? (
                    <div className='h-full w-full items-center justify-center'>
                        <ErrorMessage message={errorMessage} />
                    </div>
                ) : (
                    order.map((order) => {
                        const status = order.status.toLowerCase()
                        const isReadyForPickup = status === STATUS.forPickUp.name.toLowerCase();
                        const isPending = status === STATUS.pending.name.toLowerCase()
                        return (
                            <div 
                                key={order.orderID}
                                className={`border-l-8 rounded ${getBorderColorStyle(order.status)}`}
                            >  
                                <div className='border border-gray-200 rounded'>
                                    <div className={`bg-gray-50 px-2 py-1.5 ${openOrder === order.orderID  ? '' : 'hover:bg-gray-100'} transition`}>
                                        <div 
                                            onClick={() => {if (!isLoadingCancelledMyOrder) handleToggle(order.orderID);}}
                                            className={`${openOrder === order.orderID ? 'border-b pb-2 border-gray-300' : ''} select-none text-sm grid gap-2 grid-cols-6 cursor-pointer`}
                                        >
                                            <div className='col-span-1'>
                                                <div className='py-2 px-1 rounded flex items-center justify-center'>
                                                    <BasketIcon size='3em'/>
                                                </div>
                                            </div>
                                            <div className="flex space-y-0.5 flex-col items-start text-black text-start col-span-4">
                                                <span className="text-xs font-medium">
                                                    {`Order #${order.orderID}`}
                                                </span>
                                                <span className="text-xs">
                                                    {`${formatDate(order.createdAt)}  •  ${formatTime(order.createdAt)}`}
                                                </span>
                                                {![STATUS.forPickUp.name].includes(order.status) && (
                                                    <span
                                                        className={`text-xs font-medium px-2 py-0.5 rounded ${getTextColorStyle(order.status)}`}
                                                    >
                                                        {order.status}
                                                    </span>
                                                )}
                                                {isReadyForPickup && (
                                                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${getTextColorStyle(order.status)}`}>
                                                        Ready to pick-up
                                                    </span>
                                                )}
                                            </div>
                                            <div className='flex justify-end items-start col-span-1'>
                                                {openOrder === order.orderID ? (
                                                    <IconParkSolidUpOne height={20} width={20}/>
                                                ) : (
                                                    <IconParkSolidDownOne height={20} width={20}/>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`${openOrder === order.orderID ? 'block bg-white px-2 pb-2 w-full space-y-2' : 'hidden'}`}>
                                        <div className="flex flex-col w-full space-y-2 p-2 rounded border border-gray-300 bg-cbColor/5">
                                            <div className='flex flex-col w-full text-center space-y-0 border-b pb-2 border-gray-300'>
                                                <span className="text-[12px] uppercase font-semibold">Chapel {order?.chapels && formatChapelNumbers(order.chapels)}</span>
                                                <span className="text-[12px] capitalize">{`${formatTime(order.createdAt)} - ${order.status}`}</span>
                                            </div>
                                            <div className="-m-1.5 overflow-x-auto ">
                                                <div className="p-1 min-w-full inline-block align-middle">
                                                    <div className="overflow-hidden rounded border-b pb-2 border-gray-300">
                                                        <table className="min-w-full">
                                                            <thead className="w-full">
                                                                <tr>
                                                                    {["Qty:", "Description:"].map((col, i) => (
                                                                        <th
                                                                            key={i}
                                                                            scope="col"
                                                                            className={`px-2 py-1 text-[12px] md:text-sm font-medium whitespace-normal break-words text-left ${ col === "AMNT:" ? "text-right" : "text-left" }`}
                                                                        >
                                                                            {col}
                                                                        </th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {order?.items?.map((item, i) => {
                                                                    const productDescription = `${item.categoryName?.split(" ")[0]} | ${item.productName} (${item.size})`;
                                                                    const addons = item.addons ?? [];

                                                                    // const isExpanded = expandedItems[i];
                                                                    const itemKey = `${order.orderID}-${item.addons ?? item.productID ?? i}`;
                                                                    const visibleAddons = itemKey ? addons : addons.slice(0, 3);

                                                                    return (
                                                                        <tr key={i} className="transition-colors duration-150">
                                                                            <td className="px-2 py-0.5 align-top text-[12px]">{item.quantity}</td>
                                                                            <td className="px-2 py-0.5 text-[12px]">
                                                                                <span>{productDescription}</span>
                                                                                {addons.length > 0 && (
                                                                                    <div className="mt-1 flex flex-col">
                                                                                        {visibleAddons.map(add => (
                                                                                            <div key={add.addonID} className="flex items-center gap-2 text-xs text-gray-500">
                                                                                                <span>•</span>
                                                                                                <span>{add.addonName}</span>
                                                                                            </div>
                                                                                        ))}
                                                                                        {addons.length > 3 && (
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => toggleItem(i)}
                                                                                                className="mt-1 w-fit text-xs text-orange-600 hover:underline"
                                                                                            >
                                                                                                {itemKey ? "Show less" : "Show more"}
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='grid gap-8 grid-cols-6'>
                                                <div className='col-span-1'>
                                                    <div className="w-10 h-10 rounded-full bg-cbColor/10 flex items-center justify-center">
                                                        <PaperList size="1.5rem" className="text-cbColor" />
                                                    </div>
                                                </div>
                                                <div className="col-span-5 flex flex-col justify-center items-start">
                                                    <div className="text-xs font-medium">
                                                        Item Ordered
                                                    </div>
                                                    <div className="text-xs font-semibold">
                                                        {`${order?.items?.length ?? 0} item${(order?.items?.length ?? 0) !== 1 ? "s" : ""}`}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {isPending && (
                                            <div className='w-full flex justify-center space-x-2'>
                                                <button 
                                                    onClick={() => handleCancelledOrder(order.orderID)}
                                                    className={`${!storeOpen ? 'cursor-not-allowed' : 'cursor-pointer'} w-full text-[12px] rounded text-white py-1.5 font-medium transition-colors duration-200 ${isLoadingCancelledMyOrder ? 'bg-gray-300' : 'bg-cbColor'} `}
                                                    disabled={!storeOpen || isLoadingCancelledMyOrder}
                                                >
                                                    {isLoadingCancelledMyOrder ? (
                                                        <Loading setH={'h-[20px]'} message={'Cancelling...'}/>
                                                    ) : (
                                                        'Cancel Order'
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })
                )}
            </div>
            
            {showCancelledModal && (
                <InformationModal
                    onClose={setShowCancelledModal}
                    typeOfModal={'information'}
                    labelInfo={`Are you sure you want to cancel this order ${orderId}?`}
                    typeOfProps={'cancelledOrder'}
                    onConfirm={handleCancelOrder}
                    isLoading={isLoadingCancelledMyOrder}
                />
            )}
        </>
    )
}
